import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ProjectService } from '@/services/ProjectService';
import { AssetsConfig } from '@/services/AssetsConfig';
import { useAuthState } from '@/hooks/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import FileUploadCard from '@/components/FileUploadCard';
import { Textarea } from '@/components/ui/textarea';

const Section = ({ title, icon, children }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-blue-500"
        >
            <div className="flex items-center gap-3 mb-4">
                <div className="text-3xl p-2 bg-blue-50 rounded-full w-12 h-12 flex items-center justify-center">
                    {icon}
                </div>
                <h3 className="text-lg font-semibold">{title}</h3>
            </div>
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    {children}
                </motion.div>
            </AnimatePresence>
        </motion.div>
    );
};


const US_STATES = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

const AddressFields = ({ field, formData, handleInputChange, validationErrors, attemptedSubmit }) => {
    // console.log('AddressFields rendered with:', {
    //   field,
    //   formData,
    //   validationErrors,
    //   attemptedSubmit
    // });

    return (
        <div className="space-y-4">
            {field.structure.map((component, index) => {
                const fieldId = `${field.id}_${component.value}`;
                const isInvalid = attemptedSubmit && validationErrors[fieldId];

                // console.log('Rendering component:', {
                //   component,
                //   fieldId,
                //   isInvalid,
                //   currentValue: formData[fieldId]
                // });

                if (component.type === 'select') {
                    // Try a simple native select first to isolate the issue
                    return (
                        <div key={index} className="space-y-2">
              <Label>{component.label}{component.required && <span className="text-red-500">*</span>}</Label>
                            <select
                                value={formData[fieldId] || ''}
                onChange={(e) => {
                                    // console.log('State select change:', e.target.value);
                                    handleInputChange(fieldId, e.target.value);
                                }}
                                className="w-full p-2 border rounded-md"
                            >
                                <option value="">Select {component.label}</option>
                                {US_STATES.map(state => (
                                    <option key={state.value} value={state.value}>
                                        {state.label}
                                    </option>
                                ))}
                            </select>
                            {isInvalid && <p className="text-red-500 text-sm">{validationErrors[fieldId]}</p>}
                        </div>
                    );
                }

                return (
                    <div key={index} className="space-y-2">
            <Label>{component.label}{component.required && <span className="text-red-500">*</span>}</Label>
                        <Input
                            type="text"
                            value={formData[fieldId] || ''}
              onChange={(e) => {
                                // console.log('Input change:', {
                                //   fieldId,
                                //   value: e.target.value
                                // });
                                handleInputChange(fieldId, e.target.value);
                            }}
                            className={isInvalid ? 'border-red-500' : ''}
                            placeholder={component.label}
                        />
                        {isInvalid && <p className="text-red-500 text-sm">{validationErrors[fieldId]}</p>}
                    </div>
                );
            })}
        </div>
    );
};

const QuestionRenderer = ({
    field,
    formData,
    handleInputChange,
    validationErrors,
    attemptedSubmit,
    projectId,
    fileItems,
  setFileItems 
}) => {
  const isVisible = !field.conditions || field.conditions.every(condition => {
            const formValue = formData[condition.questionId]?.toString().toLowerCase();
            const conditionValue = condition.value.toString().toLowerCase();

            switch (condition.operator) {
                case 'not_equals':
                    return formValue !== conditionValue;
                case 'contains':
                    return formValue?.includes(conditionValue);
                case 'not_contains':
                    return !formValue?.includes(conditionValue);
                default: // equals
                    return formValue === conditionValue;
            }
        });

    if (!isVisible || field.visible === false) return null;

    const isInvalid = attemptedSubmit && validationErrors[field.id];
    const inputClassName = `mt-1 ${isInvalid ? 'border-2 border-red-500' : ''}`;

    const QuestionLabel = () => (
        <div className="flex items-center gap-2 mb-2">
            {field.icon && <span className="text-2xl">{field.icon}</span>}
            <Label>
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
        </div>
    );

    // Handle special question types first
    if (field.type === 'address') {
        return (
            <motion.div whileHover={{ scale: 1.01 }} className="space-y-2">
                <QuestionLabel />
                <AddressFields
                    field={field}
                    formData={formData}
                    handleInputChange={handleInputChange}
                    validationErrors={validationErrors}
                    attemptedSubmit={attemptedSubmit}
                />
            </motion.div>
        );
    }

    if (field.type === 'zipcode') {
        return (
            <motion.div whileHover={{ scale: 1.01 }} className="space-y-2">
                <QuestionLabel />
                <Input
                    type="text"
                    value={formData[field.id] || ''}
          onChange={(e) => {
                        const value = e.target.value.replace(/[^\d-]/g, '');
                        if (value.length <= 10) {
                            handleInputChange(field.id, value);
                        }
                    }}
                    placeholder="Enter ZIP code"
                    className={inputClassName}
                    pattern={field.validation?.pattern}
                />
        {isInvalid && (
          <p className="text-red-500 text-sm">{validationErrors[field.id]}</p>
        )}
            </motion.div>
        );
    }

    // Handle regular question types
    switch (field.type) {
        case 'radio':
            return (
                <motion.div whileHover={{ scale: 1.01 }} className="space-y-2">
                    <QuestionLabel />
                    <RadioGroup
                        value={formData[field.id] || ''}
                        onValueChange={value => handleInputChange(field.id, value)}
                        className="space-y-2"
                    >
                        {field.options?.map(option => (
                            <motion.div
                                key={option}
                                whileHover={{ scale: 1.02 }}
                                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50"
                            >
                                <RadioGroupItem value={option} id={`${field.id}-${option}`} />
                                <Label htmlFor={`${field.id}-${option}`}>{option}</Label>
                            </motion.div>
                        ))}
                    </RadioGroup>
          {isInvalid && (
            <p className="text-red-500 text-sm">{validationErrors[field.id]}</p>
          )}
                </motion.div>
            );

        case 'select':
            return (
                <motion.div whileHover={{ scale: 1.01 }} className="space-y-2">
                    <QuestionLabel />
                    <Select
                        value={formData[field.id] || ''}
                        onValueChange={value => handleInputChange(field.id, value)}
                    >
                        <SelectTrigger className={inputClassName}>
                            <SelectValue placeholder={field.placeholder} />
                        </SelectTrigger>
                        <SelectContent>
                            {field.options?.map(option => (
                <SelectItem
                  key={option}
                  value={option}
                >
                                    {option}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
          {isInvalid && (
            <p className="text-red-500 text-sm">{validationErrors[field.id]}</p>
          )}
                </motion.div>
            );

        case 'multiple_select':
            return (
                <motion.div whileHover={{ scale: 1.01 }} className="space-y-2">
                    <QuestionLabel />
                    <div className="grid grid-cols-2 gap-4">
                        {field.options?.map(option => (
                            <motion.div
                                key={option}
                                whileHover={{ scale: 1.02 }}
                                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50"
                            >
                                <Checkbox
                                    id={`${field.id}-${option}`}
                                    checked={(formData[field.id] || []).includes(option)}
                  onCheckedChange={(checked) => {
                                        const currentValues = formData[field.id] || [];
                                        const newValues = checked
                                            ? [...currentValues, option]
                                            : currentValues.filter(item => item !== option);
                                        handleInputChange(field.id, newValues);
                                    }}
                                />
                                <Label htmlFor={`${field.id}-${option}`}>{option}</Label>
                            </motion.div>
                        ))}
                    </div>
          {isInvalid && (
            <p className="text-red-500 text-sm">{validationErrors[field.id]}</p>
          )}
                </motion.div>
            );

        case 'file_upload':
            return (
                <motion.div whileHover={{ scale: 1.01 }} className="space-y-2">
                    <QuestionLabel />
                    <FileUploadCard
                        title={field.label}
                        projectId={projectId}
                        onFilesChange={files => {
                            setFileItems(files);
                            handleInputChange(field.id, files);
                        }}
                        initialFiles={fileItems}
                        cardClassName="h-full"
                        defaultCollapsed={true}
                        config={field.fileConfig}
                    />
          {isInvalid && (
            <p className="text-red-500 text-sm">{validationErrors[field.id]}</p>
          )}
                </motion.div>
            );

        default:
            return (
                <motion.div whileHover={{ scale: 1.01 }} className="space-y-2">
                    <QuestionLabel />
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
                    {field.type === 'textarea' ? (
                        <Textarea
                            value={formData[field.id] || ''}
                            onChange={e => handleInputChange(field.id, e.target.value)}
                            placeholder={field.placeholder}
                            className={`${inputClassName} min-h-[100px]`}
                            required={field.required}
                        />
                    ) : (
                        <Input
                            type={field.type}
                            value={formData[field.id] || ''}
                            onChange={e => handleInputChange(field.id, e.target.value)}
                            placeholder={field.placeholder}
                            className={inputClassName}
                            required={field.required}
                        />
                    )}
          {isInvalid && (
            <p className="text-red-500 text-sm">{validationErrors[field.id]}</p>
          )}
                </motion.div>
            );
    }
};

const StartProjectScreen = () => {
    const navigate = useNavigate();
    const { user } = useAuthState();
    const [questions, setQuestions] = useState(null);
    const [formData, setFormData] = useState({});
    const [selectedRooms, setSelectedRooms] = useState({});
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);
    const userId = user?.id || 'defaultUserId';
    const userName = user?.given_name + " " + user.family_name || '';
    const websiteUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const [validationErrors, setValidationErrors] = useState({});
    const [fileItems, setFileItems] = useState([]);

    useEffect(() => {
        const initializeForm = async () => {
            try {
                const configData = await AssetsConfig.getConfigJSON();
                setQuestions(configData);

                // Initialize with default values
                const initialData = {
          projectFor: 'self'
                };

                // If projectFor is 'self', pre-fill user data
                if (user) {
          initialData.customerName = user.given_name + " " + user.family_name;
                    initialData.email = user.email;
                }

                setFormData(initialData);

                // Fetch user data
                const response = await ProjectService.getUser(userId);
                // console.log("user", user)
                if (response.data.length === 0) {
                    // console.log("creating user")
                    await ProjectService.createUser(userId, user);
                }
            } catch (error) {
        console.error("Error initializing form:", error);
            }
        };

        initializeForm();
    }, [user, userId]);

    const handleInputChange = (fieldId, value) => {
        setFormData(prev => {
            const newData = { ...prev, [fieldId]: value };

            // Determine which section this field belongs to
            const section = questions.sections.find(section =>
                section.fields?.some(field => field.id === fieldId || fieldId.startsWith(field.id + '_'))
            );

            if (!section) {
                // If no section is found, handle it as a top-level field
                return { ...prev, [fieldId]: value };
            }

            // Initialize section object if it doesn't exist
            const sectionKey = section.title.replace(/\s+/g, '');
            newData[sectionKey] = newData[sectionKey] || {};

            if (fieldId.includes('_')) {
                // Handle address fields
                const [parentField, childField] = fieldId.split('_');
                newData[sectionKey][parentField] = newData[sectionKey][parentField] || {};
                newData[sectionKey][parentField][childField] = value;
            } else {
                // Handle regular fields
                newData[sectionKey][fieldId] = value;
            }

            return newData;
        });
    };

    const handleRoomSelection = (roomId, checked) => {
        setSelectedRooms(prev => ({
            ...prev,
            [roomId]: {
                selected: checked,
                quantity: checked ? 1 : 0
      }
        }));
    };

    const updateRoomQuantity = (roomId, action) => {
        setSelectedRooms(prev => ({
            ...prev,
            [roomId]: {
                ...prev[roomId],
        quantity: action === 'increase'
            ? (prev[roomId]?.quantity || 1) + 1
            : Math.max(1, (prev[roomId]?.quantity || 1) - 1)
      }
        }));
    };

    const validateForm = async () => {
        const errors = {};

        questions.sections.forEach(section => {
            if (section.fields) {
                section.fields.forEach(field => {
                    // Check both legacy conditionalDisplay and new conditions
                    const checkLegacyCondition = () => {
                        if (!field.conditionalDisplay) return true;
                        return formData[field.conditionalDisplay.field] === field.conditionalDisplay.value;
                    };

                    const checkNewConditions = () => {
                        if (!field.conditions?.length) return true;
                        return field.conditions.every(condition => {
                            const formValue = formData[condition.questionId];
                            const conditionValue = condition.value;

                            switch (condition.operator) {
                                case 'not_equals':
                                    return formValue !== conditionValue;
                                case 'contains':
                                    return formValue?.includes(conditionValue);
                                case 'not_contains':
                                    return !formValue?.includes(conditionValue);
                                default: // equals
                                    return formValue === conditionValue;
                            }
                        });
                    };

                    const isVisible = checkLegacyCondition() && checkNewConditions();

                    // Only validate visible required fields
                    if (isVisible && field.required) {
                        if (field.type === 'address') {
                            field.structure.forEach(component => {
                                if (component.required) {
                                    const fieldId = `${field.id}_${component.value}`;
                                    if (!formData[fieldId]) {
                                        errors[fieldId] = `${component.label} is required`;
                                    }
                                }
                            });
                        } else if (field.type === 'zipcode' && formData[field.id]) {
                            const pattern = new RegExp(field.validation?.pattern || '^[0-9]{5}(?:-[0-9]{4})?$');
                            if (!pattern.test(formData[field.id])) {
                                errors[field.id] = field.validation?.message || 'Please enter a valid ZIP code';
                            }
                        } else if (field.type === 'file_upload') {
                            if (!fileItems || fileItems.length === 0) {
                                errors[field.id] = `${field.label} is required`;
                            }
                        } else if (!formData[field.id]) {
                            errors[field.id] = `${field.label} is required`;
                        }
                    }
                });
            }
        });

        const hasSelectedRooms = Object.values(selectedRooms).some(room => room.selected);
        if (!hasSelectedRooms) {
            errors.rooms = 'Please select at least one room';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const getCustomQuestionFields = (formData, questions, fieldType) => {
        const customFields = {};

        if (fieldType === 'contactDetails') {
            // Find the ContactDetails section
            const contactDetailsSection = questions.sections.find(
                section => section.title === 'ContactDetails' || section.title.replace(/\s+/g, '') === 'ContactDetails'
            );

            if (contactDetailsSection?.fields) {
                contactDetailsSection.fields.forEach(field => {
                    // Only process fields that start with "field_"
                    if (!field.id.startsWith('field_')) return;

                    // Get the field value
                    const value = formData[field.id];

                    // Only include fields that have values
                    if (value !== undefined && value !== null && value !== '') {
                        customFields[field.id] = {
                            question: field.label,
                            answer: value,
                        };
                    }
                });
            }
        } else if (fieldType === 'projectDetails') {
            const projectDetailsSection = questions.sections.find(
                section => section.title === 'ProjectDetails' || section.title.replace(/\s+/g, '') === 'ProjectDetails'
            );

            if (projectDetailsSection?.fields) {
                projectDetailsSection?.fields.forEach(field => {
                    if (!field.id.startsWith('field_')) return;

                    // Get the field value
                    const value = formData[field.id];

                    // Only include fields that have values
                    if (value !== undefined && value !== null && value !== '') {
                        customFields[field.id] = {
                            question: field.label,
                            answer: value,
                        };
                    }
                });
            }
        }
        return customFields;
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setAttemptedSubmit(true);

        if (!validateForm()) return;

        try {
            const selectedRoomsList = Object.entries(selectedRooms)
                .filter(([_, value]) => value.selected)
                .map(([type, value]) => ({
                    type,
                    quantity: value.quantity || 1
                }));
            // console.log('fromdata', formData);
            const projectsData = selectedRoomsList.flatMap(room =>
        Array(room.quantity).fill().map(() => ({
                        type: room.type,
                        details: {
                            // ...formData, // This now includes ContactDetails and ProjectDetails
                            // contactName: userName,
                            // contactEmail: user?.email,
                            websiteUrl: websiteUrl,
                            fileItems: fileItems,
                        },
                    }))
            );

            const collectionData = {
                name: formData.collectionName || 'Room Renovation',
                websiteUrl: websiteUrl,
                details: {
                    ContactDetails: {
                        projectFor:
                            formData.projectFor === 'For me'
                                ? 'For me'
                                : { for: formData.projectFor, name: formData.customerName, email: formData.email },
                        contactName: user.given_name + ' ' + user.family_name,
                        contactEmail: user.email,
                        fields: {
                            ...getCustomQuestionFields(formData, questions, 'contactDetails'),
                        },
                    },

                    projectDetails: {
                        city: Object.entries(formData).find(([key]) => key.endsWith('_city'))?.[1],
                        state: Object.entries(formData).find(([key]) => key.endsWith('_state'))?.[1],
                        street1: Object.entries(formData).find(([key]) => key.endsWith('_street1'))?.[1],
                        street2: Object.entries(formData).find(([key]) => key.endsWith('_street2'))?.[1],
                        zipCode: Object.entries(formData).find(([key]) => key.endsWith('_zipCode'))?.[1],
                        fields: {
                            ...getCustomQuestionFields(formData, questions, 'projectDetails'),
                        },
                    },
                },
                description: `Collection of ${projectsData.length} room${projectsData.length > 1 ? 's' : ''}`,
            };

            const response = await ProjectService.createProjectCollection(user?.id, projectsData, collectionData);

            navigate(`/project/${response.projects[0].id}`);
        } catch (error) {
            console.error('Error creating project:', error);
        }
    };

    if (!questions) return null;

    return (
        <div className="min-h-screen bg-gray-50 py-12">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="container mx-auto max-w-3xl px-4"
            >
                <Card>
                    <CardHeader className="text-center">
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} transition={{ duration: 0.5 }}>
                            <CardTitle className="text-3xl bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">
                                Start Your Project
                            </CardTitle>
              <p className="text-gray-600 mt-2">
                Tell us about your renovation plans
              </p>
                        </motion.div>
                    </CardHeader>

                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {questions.sections.map(section => (
                                <Section key={section.id} title={section.title} icon={section.icon || ''}>
                                    {section.type === 'radio' && (
                                        <RadioGroup
                                            value={formData[section.id] || ''}
                                            onValueChange={value => handleInputChange(section.id, value)}
                                            className="space-y-2"
                                        >
                                            {section.options.map(option => (
                                                <motion.div
                                                    key={option.id}
                                                    whileHover={{ scale: 1.02 }}
                                                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50"
                                                >
                                                    <RadioGroupItem value={option.value} id={option.id} />
                                                    <Label htmlFor={option.id}>{option.label}</Label>
                                                </motion.div>
                                            ))}
                                        </RadioGroup>
                                    )}

                                    {section.type === 'group' && section.fields && (
                                        <div className="space-y-4">
                                            {section.fields.map(field => (
                                                <QuestionRenderer
                                                    key={field.id}
                                                    field={field}
                                                    formData={formData}
                                                    handleInputChange={handleInputChange}
                                                    validationErrors={validationErrors}
                                                    attemptedSubmit={attemptedSubmit}
                                                    fileItems={fileItems}
                                                    setFileItems={setFileItems}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {section.type === 'checkboxGroup' && (
                                        <div className="space-y-4">
                                            {validationErrors.rooms && attemptedSubmit && (
                                                <p className="text-red-500 text-sm">{validationErrors.rooms}</p>
                                            )}
                                            {section.options.map(option => (
                                                <motion.div
                                                    key={option.id}
                                                    whileHover={{ scale: 1.01 }}
                                                    className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm"
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-2">
                                                            <Checkbox
                                                                id={option.id}
                                                                checked={selectedRooms[option.id]?.selected || false}
                                onCheckedChange={(checked) => handleRoomSelection(option.id, checked)}
                                                            />
                                                            <Label htmlFor={option.id}>{option.label}</Label>
                                                        </div>

                                                        {option.allowMultiple && selectedRooms[option.id]?.selected && (
                                                            <div className="flex items-center space-x-2">
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    size="sm"
                                  onClick={() => updateRoomQuantity(option.id, 'decrease')}
                                                                    disabled={selectedRooms[option.id]?.quantity <= 1}
                                                                    className="h-8 w-8 p-0"
                                                                >
                                                                    -
                                                                </Button>
                                                                <span className="w-8 text-center">
                                                                    {selectedRooms[option.id]?.quantity || 1}
                                                                </span>
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    size="sm"
                                  onClick={() => updateRoomQuantity(option.id, 'increase')}
                                                                    className="h-8 w-8 p-0"
                                                                >
                                                                    +
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    )}
                                </Section>
                            ))}

                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5 }}
                                className="flex justify-end pt-6"
                            >
                <Button 
                  type="submit" 
                  size="lg"
                  className="w-full sm:w-auto"
                >
                                    Start Project
                                </Button>
                            </motion.div>
                        </form>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
};

export default StartProjectScreen;